import { CurrencyPipe, PercentPipe } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  isDevMode,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideEnvironmentInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter, Router, withViewTransitions } from '@angular/router';
import { Configuration } from '@api';
import { provideTransloco, provideTranslocoTranspiler } from '@jsverse/transloco';
import { provideTranslocoLocale } from '@jsverse/transloco-locale';
import * as Sentry from '@sentry/angular';
import { CookieService } from 'ngx-cookie-service';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { ToastModule } from 'primeng/toast';
import { environment } from '@environments/environment';
import { routes } from './app.routes';
import { languageInterceptor } from './interceptors/language.interceptor';
import { NoEvalTranslocoTranspiler } from './no-eval-transloco-transpiler';
import { SlimPreset } from './primeng-slim-preset';
import { EduTranslocoLoader, initTranslations } from './transloco-loader';
import { AvailableLocale, LANG_TO_LOCALE_MAPPING } from '@helpers/locale';
import { GrowthBookService } from '@shared/services/growthbook/growth-book.service';
import { LocalStorageService } from '@shared/services/local-storage.service';
import { EduNewsService } from '@shared/services/news.service';

export const EDU_TRANSLOCO_CONFIG = {
  availableLangs: [AvailableLocale.DE, AvailableLocale.EN],
  defaultLang: AvailableLocale.DE,
  reRenderOnLangChange: true,
  prodMode: !isDevMode(),
  missingHandler: { logMissingKey: isDevMode() },
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes, withViewTransitions()),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    provideHttpClient(withFetch(), withInterceptors([languageInterceptor])),
    provideTransloco({
      config: EDU_TRANSLOCO_CONFIG,
      loader: EduTranslocoLoader,
    }),
    provideAppInitializer(initTranslations),
    provideTranslocoTranspiler(NoEvalTranslocoTranspiler),
    provideTranslocoLocale({ langToLocaleMapping: LANG_TO_LOCALE_MAPPING }),

    {
      provide: Configuration,
      useFactory: () => {
        return new Configuration({
          basePath: environment.apiUrl,
          withCredentials: true,
        });
      },
    },
    providePrimeNG({
      theme: {
        preset: SlimPreset,
        options: {
          // This is needed to apply dark mode styles in primeng components, which need to be the same class as in index.html
          darkModeSelector: '.theme-dark',
          cssLayer: {
            name: 'primeng',
            order: 'theme, base, primeng',
          },
        },
      },
    }),
    provideEnvironmentInitializer(() => {
      inject(GrowthBookService).init({
        apiHost: environment.growthbook.apiHost,
        clientKey: environment.growthbook.clientKey,
        remoteEval: environment.growthbook.remoteEval,
        enableDevMode: environment.growthbook.enableDevMode,
      });
    }),
    // Services
    GrowthBookService,
    CookieService,
    EduNewsService,
    LocalStorageService,
    ToastModule,
    MessageService,
    // Pipes
    CurrencyPipe,
    PercentPipe,
  ],
};
